import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { IDENTIA_URL } from '../../config/utils'
import type { RootState } from '../store'

// Identia request/response types
export interface ValidateAuthRequest {
    token: string
}

export interface ValidateAuthResponse {
    hasAccess: boolean
}

export interface SigninCredentials {
    typeCredential: string
    credential: string
    password: string
}

export interface SigninRequest {
    userType: string
    base: string
    credentials: SigninCredentials
}

export interface IdentiaEntityCustomize {
    logo?: string
    portrait?: string
    colors?: { [key: string]: string }
}

export interface IdentiaEntity {
    id: string
    brand_name: string
    legal_name: string
    domain?: string
}

export interface IdentiaPlatform {
    id: string
    platform_name: string
    alias: string
    description?: string
}

export interface IdentiaPlatformRole {
    id: string
    name: string
    slug: string
    scope?: string
    context_type?: string
    context_id?: string
    isDefault?: boolean
}

export interface IdentiaUser {
    id: string
    userType: string
    attributes: { [key: string]: any }
}

export interface SigninResponse {
    token: string
    user: IdentiaUser
    roles: string[]
    platform: IdentiaPlatform
    entities: IdentiaEntity[]
    multipleEntities: boolean
}

export interface SigninEntityRequest {
    entity: string
    token: string
}

export type SigninEntityResponse = SigninResponse

// --- New: Get users by entity response/request types ---
export interface IdentiaUserType {
    id: string
    name: string
}

export interface IdentiaUserAttributes {
    name?: string
    lastname?: string
    description?: string
    dni?: string
    genre?: string
    place_of_birth?: string
    birthdate?: string | null
    phone?: string
    cv_url?: string
    address?: string
    linkedin_url?: string
    photo?: string
    rol?: string
    documentType?: string
    email_verification?: boolean
    email?: string
    nickname?: string
    [k: string]: any
}

export interface IdentiaUserItem {
    id: string
    userType: IdentiaUserType
    attributes: IdentiaUserAttributes
    created_at?: string
    updated_at?: string
    createdAt?: string
    updatedAt?: string
    platform?: {
        id: string
        name: string
        alias: string
        description: string
        roles: IdentiaPlatformRole[]
    }
    entity?: {
        id: string
        brand_name: string
        legal_name: string
        domain: string
    }
    roles?: {
        id: string
        slug: string
        name: string
    }[]
}

export const IDENTIA_USER_TYPES = {
    PERSONA: 'Persona'
} as const

export interface CreateUserRequest {
    userType?: string
    attributes: IdentiaUserAttributes
    platform_id?: string
    entity_id?: string
    role_id?: string
}

export interface CreateUserResponse {
    id: string
}

export type IdentiaRoleContext =
    | 'system'
    | 'platform'
    | 'entity'
    | 'area'
    | 'team'
    | 'service'
    | 'app'

export interface AssignRoleRequest {
    userId: string
    role_id: string
    context_type: IdentiaRoleContext
    context_id: string
    expires_at?: string
}

export interface AssignRoleResponse {
    id: string
    user_id: string
    role_id: string
    context_type: IdentiaRoleContext
    context_id: string
    assigned_by: string
    expires_at?: string
    is_active: boolean
    inherits: boolean
}

export interface RemoveRoleRequest {
    userId: string
    roleId: string
    context_type: IdentiaRoleContext
    context_id: string
}

export interface GetUserRolesParams {
    userId: string
    context_type?: IdentiaRoleContext
    context_id?: string
}

export interface UserRoleAssignment {
    _id: string
    user_id: string
    role_id: string
    context_type: IdentiaRoleContext
    context_id: string
    assigned_by: string
    assigned_at?: string
    is_active: boolean
    expires_at?: string
    inherits?: boolean
}

export interface UserRoleResponseItem {
    id: string
    name: string
    slug: string
    level?: number
    assignment: UserRoleAssignment
    context?: any
}

export interface RecoveryLinkRequest {
    userId: string
    base: string
}

export interface RecoveryLinkResponse {
    token: string
    url: string
}

export interface ValidateRecoveryTokenRequest {
    token: string
}

export interface ValidateRecoveryTokenResponse {
    valid: boolean
    user: {
        id: string
        email: string
    }
}

export interface SendRecoveryPasswordRequest {
    email: string
    base: string
}

export interface SendRecoveryPasswordResponse {
    response: string
}

export interface ResetPasswordRequest {
    token: string
    password: string
}

export interface ResetPasswordResponse {
    password_changed: boolean
}

export interface UpdateUserRequest {
    userId: string
    attributes?: Partial<IdentiaUserAttributes>
    platformId?: string
    entityId?: string
    roleId?: string
    expiresAt?: string
}

export interface GetEntityUsersParams {
    entityId: string
    page?: number
    limit?: number
    search?: string
    role?: string
    sortBy?: 'name' | 'lastname' | 'email' | 'nickname'
    order?: 'asc' | 'desc'
}

export interface GetEntityUsersResponse {
    users: IdentiaUserItem[]
    total: number
    page: number
    limit: number
    platform?: IdentiaPlatform
}

export interface GetRolesParams {
    context_type: string
    context_id: string
}

export interface RegistrationCheckParams {
    typeCredential: string
    credential: string
    platform: string
    entity: string
}

export interface RegistrationCheckUser {
    id: string
    attributes: IdentiaUserAttributes
}

export interface RegistrationCheckResponse {
    isRegistered: boolean
    isRegisteredInPlatform: boolean
    isRegisteredInEntity: boolean
    hasAccessInEntity: boolean
    user: RegistrationCheckUser | null
}

export const identiaApi = createApi({
    reducerPath: 'identiaApi',
    baseQuery: fetchBaseQuery({
        baseUrl: `${IDENTIA_URL}/api`,
        prepareHeaders: (headers, { getState }) => {
            const token = (getState() as RootState).auth.token
            if (token) headers.set('authorization', `Bearer ${token}`)
            return headers
        },
    }),
    tagTypes: ['Identia'],
    endpoints: (builder) => ({
        validateAuth: builder.mutation<ValidateAuthResponse, ValidateAuthRequest>({
            query: (body) => ({
                url: '/auth/validate-auth',
                method: 'POST',
                body,
            }),
        }),
        signin: builder.mutation<SigninResponse, SigninRequest>({
            query: (body) => ({
                url: '/auth/signin',
                method: 'POST',
                body,
            }),
        }),
        signinEntity: builder.mutation<SigninEntityResponse, SigninEntityRequest>({
            query: ({ entity, token }) => ({
                url: '/auth/signin/entity',
                method: 'POST',
                body: { entity },
                headers: token ? { authorization: `Bearer ${token}` } : undefined,
            }),
        }),
        getEntityUsers: builder.query<GetEntityUsersResponse, GetEntityUsersParams>({
            query: (arg: GetEntityUsersParams) => {
                const { entityId, role, page = 1, limit = 20, search, sortBy = 'name', order = 'asc' } = arg
                const params = new URLSearchParams()
                params.append('page', String(Math.max(1, page)))
                params.append('limit', String(Math.min(100, Math.max(0, limit))))
                if (search) params.append('search', search)
                if (role) params.append('role', role)
                if (sortBy) params.append('sortBy', sortBy)
                if (order) params.append('order', order)

                let platformId = 'multiedu'
                try {
                    const raw = localStorage.getItem('user')
                    const parsed = raw ? JSON.parse(raw) : null
                    if (parsed && parsed.platform && parsed.platform.id) platformId = parsed.platform.id
                } catch (e) {
                    /* ignore and use default */
                }

                return {
                    url: `/platforms/${platformId}/entities/${entityId}/users?${params.toString()}`,
                    method: 'GET',
                }
            },
            providesTags: (result) =>
                result
                    ? [
                          ...result.users.map(({ id }) => ({ type: 'Identia' as const, id })),
                          { type: 'Identia', id: 'LIST' },
                      ]
                    : [{ type: 'Identia', id: 'LIST' }],
        }),
        registrationCheck: builder.query<RegistrationCheckResponse, RegistrationCheckParams>({
            query: ({ typeCredential, credential, platform, entity }) => {
                const params = new URLSearchParams({
                    typeCredential,
                    credential,
                    platform,
                    entity,
                })

                return {
                    url: `/users/registration/check?${params.toString()}`,
                    method: 'GET',
                }
            },
        }),
        getPlatformRoles: builder.query<IdentiaPlatformRole[], { platformId: string }>({
            query: ({ platformId }) => ({
                url: `/platforms/${platformId}/roles`,
                method: 'GET',
            }),
        }),
        getRoles: builder.query<IdentiaPlatformRole[], GetRolesParams>({
            query: (params) => ({
                url: `/roles`,
                method: 'GET',
                params,
            }),
        }),
        createUser: builder.mutation<CreateUserResponse, CreateUserRequest>({
            query: (body) => ({
                url: '/users/',
                method: 'POST',
                body: {
                    userType: IDENTIA_USER_TYPES.PERSONA,
                    ...body,
                },
            }),
            invalidatesTags: [{ type: 'Identia', id: 'LIST' }],
        }),
        assignRoleToUser: builder.mutation<AssignRoleResponse, AssignRoleRequest>({
            query: ({ userId, ...body }) => ({
                url: `/users/${userId}/roles`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error, { userId }) => [
                { type: 'Identia', id: userId },
                { type: 'Identia', id: 'LIST' },
            ],
        }),
        removeRoleFromUser: builder.mutation<void, RemoveRoleRequest>({
            query: ({ userId, roleId, context_type, context_id }) => {
                const params = new URLSearchParams({
                    context_type,
                    context_id,
                })
                return {
                    url: `/users/${userId}/roles/${roleId}?${params.toString()}`,
                    method: 'DELETE',
                }
            },
            invalidatesTags: (result, error, { userId }) => [
                { type: 'Identia', id: userId },
                { type: 'Identia', id: 'LIST' },
            ],
        }),
        updateUser: builder.mutation<IdentiaUserItem, UpdateUserRequest>({
            query: ({ userId, ...body }) => ({
                url: `/users/${userId}`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (result, error, { userId }) => [
                { type: 'Identia', id: userId },
                { type: 'Identia', id: 'LIST' },
            ],
        }),
        generateRecoveryLink: builder.mutation<RecoveryLinkResponse, RecoveryLinkRequest>({
            query: ({ userId, ...body }) => ({
                url: `/users/${userId}/generate-recovery-link`,
                method: 'POST',
                body,
            }),
        }),
        validateRecoveryToken: builder.mutation<
            ValidateRecoveryTokenResponse,
            ValidateRecoveryTokenRequest
        >({
            query: (body) => ({
                url: '/auth/validate-recovery-token',
                method: 'POST',
                body,
            }),
        }),
        sendRecoveryPassword: builder.mutation<
            SendRecoveryPasswordResponse,
            SendRecoveryPasswordRequest
        >({
            query: (body) => ({
                url: '/auth/send-recovery-password',
                method: 'POST',
                body,
            }),
        }),
        resetPassword: builder.mutation<ResetPasswordResponse, ResetPasswordRequest>({
            query: ({ token, ...body }) => ({
                url: `/auth/recovery-password?token=${token}`,
                method: 'PATCH',
                body,
            }),
        }),
        getUserById: builder.query<
            IdentiaUserItem,
            { userId: string; platform_id?: string; entity_id?: string }
        >({
            query: ({ userId, platform_id, entity_id }) => {
                const params = new URLSearchParams()
                if (platform_id) params.append('platform_id', platform_id)
                if (entity_id) params.append('entity_id', entity_id)

                return {
                    url: `/users/${userId}${params.toString() ? `?${params.toString()}` : ''}`,
                    method: 'GET',
                }
            },
            providesTags: (result, error, { userId }) => [{ type: 'Identia', id: userId }],
        }),
        getUserRoles: builder.query<UserRoleResponseItem[], GetUserRolesParams>({
            query: ({ userId, context_type, context_id }) => {
                const params = new URLSearchParams()
                if (context_type) params.append('context_type', context_type)
                if (context_id) params.append('context_id', context_id)

                return {
                    url: `/users/${userId}/roles${params.toString() ? `?${params.toString()}` : ''}`,
                    method: 'GET',
                }
            },
            providesTags: (result, error, { userId }) => [
                { type: 'Identia', id: userId },
                { type: 'Identia', id: 'LIST' },
            ],
        }),
    }),
})

export const {
    useValidateAuthMutation,
    useSigninMutation,
    useSigninEntityMutation,
    useGetEntityUsersQuery,
    useLazyGetEntityUsersQuery,
    useRegistrationCheckQuery,
    useLazyRegistrationCheckQuery,
    useGetPlatformRolesQuery,
    useLazyGetPlatformRolesQuery,
    useCreateUserMutation,
    useAssignRoleToUserMutation,
    useRemoveRoleFromUserMutation,
    useUpdateUserMutation,
    useGenerateRecoveryLinkMutation,
    useValidateRecoveryTokenMutation,
    useSendRecoveryPasswordMutation,
    useResetPasswordMutation,
    useGetUserByIdQuery,
    useLazyGetUserByIdQuery,
    useGetRolesQuery,
    useLazyGetRolesQuery,
    useGetUserRolesQuery,
    useLazyGetUserRolesQuery,
} = identiaApi

export default identiaApi
